import { Injectable, signal } from '@angular/core';

export interface ConfirmRequest {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** 'danger' for anything that deletes or throws work away. */
  tone?: 'default' | 'danger';
}

interface PendingConfirm extends ConfirmRequest {
  resolve: (confirmed: boolean) => void;
}

/**
 * CONFIRM SERVICE
 * ---------------
 * One app-wide accept/cancel popup, rendered by `ConfirmDialogComponent` in the
 * app shell. Anything that needs a yes before it acts awaits `ask()`:
 *
 *   if (!(await this.confirm.ask({ title: 'Remove this fixture?', ... }))) return;
 *
 * Only one question is ever on screen. Asking a second while the first is
 * open answers the first "no", so no caller is left waiting forever.
 */
@Injectable({ providedIn: 'root' })
export class ConfirmService {

  private readonly _pending = signal<PendingConfirm | null>(null);
  readonly pending = this._pending.asReadonly();

  ask(request: ConfirmRequest): Promise<boolean> {
    this._pending()?.resolve(false);
    return new Promise(resolve => this._pending.set({ ...request, resolve }));
  }

  answer(confirmed: boolean): void {
    const pending = this._pending();
    if (!pending) return;
    this._pending.set(null);
    pending.resolve(confirmed);
  }
}
